import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslate } from "@refinedev/core";

import {
  fetchNazorgWizard,
  statusOf,
  submitNazorgInzending,
  type NazorgAntwoord,
  type NazorgHuisstijl,
  type NazorgVraag,
  type NazorgWizard,
} from "../../nazorg/nazorgApi";
import { isAnswered, VraagControl } from "../../nazorg/VraagControl";
import { isNazorgPath, readNazorgToken } from "../../nazorg/nazorgRoute";

// The adopter aftercare portal — the platform's only entry point for filling in a nazorg
// questionnaire (design handoff). Reached via a tokenised mail/WhatsApp link (`/nazorg/<token>`), no
// account: the token is read once, then stripped from the address bar. Off one flat array of questions
// it renders the handoff's three screens — welcome → questions → thank-you — in either a step-by-step
// (context panel + section stepper, one question per screen) or an all-on-one-page flow. Answers are
// auto-saved locally ("opslaan & later verder") and POSTed as one JSON submission; red-flag signalling
// and welfare scoring are server-side.

type Phase = "loading" | "error" | "expired" | "ready";

const STORAGE_PREFIX = "nazorg-antwoorden-v1:";
const TOAST_MS = 3200;

/** The i18nProvider ignores interpolation options, so fill `{naam}`/`{n}`-style placeholders ourselves. */
function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? ""));
}

interface Section {
  title: string;
  qs: NazorgVraag[];
}

/** Group the flat question array into its sections (a new section starts on `sectionStart`). */
function toSections(vragen: NazorgVraag[]): Section[] {
  const sections: Section[] = [];
  for (const q of vragen) {
    if (q.sectionStart || sections.length === 0) {
      sections.push({ title: q.section, qs: [] });
    }
    sections[sections.length - 1].qs.push(q);
  }
  return sections;
}

export function NazorgPortal() {
  const t = useTranslate();
  // Capture the token before the address bar is cleaned (initializer runs once). Accepts both the
  // path-style link (`/nazorg/<token>`) and a `?token=` query link (e.g. a backoffice-issued
  // `…/nazorg/index.html?token=<token>`).
  const [token] = useState(() =>
    readNazorgToken(globalThis.location?.pathname, globalThis.location?.search),
  );
  const [phase, setPhase] = useState<Phase>("loading");
  const [wizard, setWizard] = useState<NazorgWizard | null>(null);
  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, NazorgAntwoord>>({});
  const [hasSaved, setHasSaved] = useState(false);
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const storageKey = token ? STORAGE_PREFIX + token : "";

  // Strip the token from the URL (path segment AND/OR ?token= query) so a copied/shared link can't
  // replay the invitation.
  useEffect(() => {
    const loc = globalThis.location;
    if (token && loc && isNazorgPath(loc.pathname) && globalThis.history?.replaceState) {
      globalThis.history.replaceState(null, "", "/nazorg");
    }
  }, [token]);

  // Resolve the token → questionnaire definition + context.
  useEffect(() => {
    if (!token) {
      setPhase("expired");
      return;
    }
    let active = true;
    setPhase("loading");
    void (async () => {
      try {
        const data = await fetchNazorgWizard(token);
        if (active) {
          setWizard(data);
          setPhase("ready");
        }
      } catch (error) {
        if (!active) {
          return;
        }
        const status = statusOf(error);
        // A spent/unknown/expired invitation answers 404/410 — show the friendly "link no longer
        // valid" screen rather than a generic error with a pointless retry.
        setPhase(status === 404 || status === 410 ? "expired" : "error");
      }
    })();
    return () => {
      active = false;
    };
  }, [token, reloadKey]);

  // Resume a saved session: pull any locally-stored answers + position once the wizard is ready.
  useEffect(() => {
    if (!wizard || !storageKey) {
      return;
    }
    try {
      const raw = globalThis.localStorage?.getItem(storageKey);
      if (!raw) {
        return;
      }
      const saved = JSON.parse(raw) as { answers?: Record<string, NazorgAntwoord>; idx?: number };
      const savedAnswers = saved.answers ?? {};
      setAnswers(savedAnswers);
      setIdx(typeof saved.idx === "number" ? saved.idx : 0);
      setHasSaved(Object.keys(savedAnswers).length > 0);
    } catch {
      // Corrupt/again-unavailable storage: just start fresh.
    }
  }, [wizard, storageKey]);

  // Auto-save: persist answers + position on every change ("Je antwoorden worden automatisch bewaard").
  // Skipped before there's anything to save, and once submitted (the session has been cleared).
  useEffect(() => {
    if (!wizard || !storageKey || submitted) {
      return;
    }
    if (!started && Object.keys(answers).length === 0) {
      return;
    }
    try {
      globalThis.localStorage?.setItem(storageKey, JSON.stringify({ answers, idx }));
    } catch {
      // Storage unavailable (private mode / quota) — auto-save is best-effort.
    }
  }, [answers, idx, started, submitted, wizard, storageKey]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const sections = useMemo(() => toSections(wizard?.vragen ?? []), [wizard]);

  const shell = (children: React.ReactNode, save?: boolean) => (
    <PortalShell wizard={wizard} showSave={!!save} onSave={saveLater}>
      {children}
    </PortalShell>
  );

  if (phase === "loading") {
    return shell(<p className="nazorg-state">{t("state.loading")}</p>);
  }
  if (phase === "expired") {
    return shell(
      <div className="nazorg-state nazorg-state--center" role="alert">
        <h2>{t("nazorg.expired.title")}</h2>
        <p>{t("nazorg.expired.body")}</p>
      </div>,
    );
  }
  if (phase === "error" || !wizard) {
    return shell(
      <div className="nazorg-state nazorg-state--center" role="alert">
        <h2>{t("state.error.title")}</h2>
        <p>{t("state.error.body")}</p>
        <button type="button" className="nazorg-primary" onClick={() => setReloadKey((k) => k + 1)}>
          {t("state.error.retry")}
        </button>
      </div>,
    );
  }

  const Q = wizard.vragen;
  const N = Q.length;
  const isStep = wizard.weergave !== "pagina";
  const safeIdx = Math.min(idx, Math.max(0, N - 1));
  const cur = Q[safeIdx];
  const petName = wizard.dierNaam || t("nazorg.fallbackName");
  const answeredCount = Q.filter((q) => isAnswered(q, answers[q.id])).length;
  const progressPct = N === 0 ? 0 : Math.round(((isStep ? safeIdx + 1 : answeredCount) / N) * 100);

  function setAnswer(id: string, value: NazorgAntwoord) {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  }

  function saveLater() {
    if (storageKey) {
      try {
        globalThis.localStorage?.setItem(storageKey, JSON.stringify({ answers, idx }));
      } catch {
        // best-effort
      }
    }
    setHasSaved(true);
    setToast(t("nazorg.savedToast"));
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), TOAST_MS);
  }

  function clearSaved() {
    try {
      globalThis.localStorage?.removeItem(storageKey);
    } catch {
      // best-effort
    }
  }

  async function onSubmit() {
    setSubmitError(undefined);
    setBusy(true);
    try {
      const filled = Object.fromEntries(
        Object.entries(answers).filter(
          ([, v]) => v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0),
        ),
      );
      await submitNazorgInzending(token, { antwoorden: filled });
      clearSaved();
      setSubmitted(true);
    } catch (error) {
      const status = statusOf(error);
      setSubmitError(
        status === 404 || status === 410 ? t("nazorg.expired.body") : t("nazorg.submitError"),
      );
    } finally {
      setBusy(false);
    }
  }

  function onNext() {
    setSubmitError(undefined);
    if (safeIdx >= N - 1) {
      void onSubmit();
    } else {
      setIdx(safeIdx + 1);
    }
  }

  // ── thank-you ───────────────────────────────────────────────────────────
  if (submitted) {
    return shell(
      <div className="nazorg-done">
        <div className="nazorg-done__check" aria-hidden="true">
          ✓
        </div>
        <h1 className="nazorg-done__title">{t("nazorg.done.title")}</h1>
        <p className="nazorg-done__lede">
          {fill(wizard?.huisstijl?.slotTekst ?? t("nazorg.done.lede"), { naam: petName })}
        </p>
        <button
          type="button"
          className="nazorg-done__review"
          onClick={() => {
            setSubmitted(false);
            setStarted(true);
            setIdx(0);
          }}
        >
          {t("nazorg.done.review")}
        </button>
      </div>,
    );
  }

  // ── welcome ─────────────────────────────────────────────────────────────
  if (!started) {
    return shell(
      <div className="nazorg-welcome">
        {wizard.dierFotoUrl ? (
          <img className="nazorg-welcome__photo" src={wizard.dierFotoUrl} alt={petName} />
        ) : (
          <div className="nazorg-welcome__photo" aria-hidden="true" />
        )}
        <div className="nazorg-welcome__body">
          <div className="nazorg-kicker">{t("nazorg.welcome.kicker")}</div>
          {/* De titel en de welkomsttekst van het asiel (Lay-out en thema) gaan voor; daarna de tekst
              die bij deze uitnodiging hoort, en anders de standaardtekst van het portaal. */}
          <h1 className="nazorg-welcome__title">
            {fill(wizard.huisstijl?.kopTitel ?? t("nazorg.welcome.title"), { naam: petName })}
          </h1>
          <p className="nazorg-welcome__lede">
            {fill(wizard.huisstijl?.welkomTekst ?? wizard.intro ?? t("nazorg.welcome.lede"), { naam: petName })}
          </p>
          <div className="nazorg-meta">
            <span className="nazorg-meta__item">
              <span className="nazorg-meta__dot" aria-hidden="true" />
              {fill(t("nazorg.welcome.count"), { n: N })}
            </span>
            <span className="nazorg-meta__item">
              <span className="nazorg-meta__dot" aria-hidden="true" />
              {t("nazorg.welcome.duration")}
            </span>
          </div>
          <button type="button" className="nazorg-primary" onClick={() => setStarted(true)}>
            {hasSaved ? t("nazorg.resume") : t("nazorg.start")}
          </button>
          {hasSaved ? <p className="nazorg-welcome__saved">{t("nazorg.welcome.savedNote")}</p> : null}
        </div>
      </div>,
      true,
    );
  }

  // ── all-on-one-page flow ──────────────────────────────────────────────────
  if (!isStep) {
    let sectionNum = 0;
    return shell(
      <div className="nazorg-onepage">
        <h1 className="nazorg-onepage__title">{fill(t("nazorg.welcome.title"), { naam: petName })}</h1>
        <p className="nazorg-onepage__sub">{t("nazorg.onepage.subtitle")}</p>
        <div className="nazorg-sticky">
          <span className="nazorg-sticky__count">
            {fill(t("nazorg.onepage.answered"), { x: answeredCount, total: N })}
          </span>
          <div className="nazorg-bar">
            <div className="nazorg-bar__fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
        <div className="nazorg-blocks">
          {Q.map((q) => {
            if (q.sectionStart) {
              sectionNum += 1;
            }
            return (
              <div key={q.id}>
                {q.sectionStart ? (
                  <div className="nazorg-section">
                    <span className="nazorg-section__num">{String(sectionNum).padStart(2, "0")}</span>
                    <span className="nazorg-section__title">{q.section}</span>
                    <span className="nazorg-section__rule" aria-hidden="true" />
                  </div>
                ) : null}
                <div className="nazorg-block">
                  <h3 className="nazorg-block__title">{fill(q.q, { naam: petName })}</h3>
                  {q.helper ? <p className="nazorg-block__helper">{q.helper}</p> : null}
                  <div className="nazorg-card__control">
                    <VraagControl vraag={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} token={token} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {submitError ? (
          <p className="nazorg-footer__error" role="alert">
            {submitError}
          </p>
        ) : null}
        <button type="button" className="nazorg-primary" disabled={busy} onClick={() => void onSubmit()}>
          {busy ? t("nazorg.submitting") : t("nazorg.submit")}
        </button>
        <Toast text={toast} />
      </div>,
      true,
    );
  }

  // ── step-by-step flow ─────────────────────────────────────────────────────
  const curSecIdx = sections.findIndex((s) => s.qs.some((q) => q.id === cur.id));
  return shell(
    <div className="nazorg-step">
      <aside className="nazorg-aside">
        {wizard.dierFotoUrl ? (
          <img className="nazorg-aside__photo" src={wizard.dierFotoUrl} alt={petName} />
        ) : (
          <div className="nazorg-aside__photo" aria-hidden="true" />
        )}
        <div className="nazorg-aside__head">
          <div className="nazorg-kicker">{t("nazorg.context.kicker")}</div>
          <h2 className="nazorg-aside__name">{petName}</h2>
          <p className="nazorg-aside__sub">{fill(t("nazorg.context.subtitle"), { naam: petName })}</p>
        </div>
        <div className="nazorg-stepper">
          {sections.map((s, i) => {
            const status = i < curSecIdx ? "done" : i === curSecIdx ? "active" : "upcoming";
            const ans = s.qs.filter((q) => isAnswered(q, answers[q.id])).length;
            return (
              <div key={s.title + i} className="nazorg-stepper__row">
                <div className="nazorg-stepper__rail">
                  <span className={`nazorg-stepper__dot is-${status}`}>
                    {status === "done" ? "✓" : i + 1}
                  </span>
                  {i < sections.length - 1 ? (
                    <span
                      className={"nazorg-stepper__line" + (i < curSecIdx ? " is-done" : "")}
                      aria-hidden="true"
                    />
                  ) : null}
                </div>
                <div className="nazorg-stepper__body">
                  <div className={`nazorg-stepper__title is-${status}`}>{s.title}</div>
                  <div className="nazorg-stepper__meta">
                    {fill(t("nazorg.context.sectionMeta"), { x: ans, y: s.qs.length })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {wizard.telefoon || wizard.opvangEmail ? (
          <div className="nazorg-aside__help">
            <span className="nazorg-aside__helpLabel">{t("nazorg.context.helpTitle")}</span>
            {wizard.telefoon ? (
              <a className="nazorg-aside__phone" href={`tel:${wizard.telefoon}`}>
                {wizard.telefoon}
              </a>
            ) : null}
            {wizard.opvangEmail ? (
              <a className="nazorg-aside__email" href={`mailto:${wizard.opvangEmail}`}>
                {wizard.opvangEmail}
              </a>
            ) : null}
          </div>
        ) : null}
      </aside>

      <div className="nazorg-q">
        <div className="nazorg-q__head">
          <span className="nazorg-q__counter">
            {t("nazorg.question")} {safeIdx + 1}{" "}
            <span className="nazorg-q__counterTotal">/ {N}</span>
          </span>
          <span className="nazorg-q__section">{cur.section}</span>
        </div>
        <div className="nazorg-bar">
          <div className="nazorg-bar__fill" style={{ width: `${progressPct}%` }} />
        </div>

        <div className="nazorg-card" key={safeIdx}>
          <h2 className="nazorg-card__title">{fill(cur.q, { naam: petName })}</h2>
          {cur.helper ? <p className="nazorg-card__helper">{cur.helper}</p> : null}
          <div className="nazorg-card__control">
            <VraagControl vraag={cur} value={answers[cur.id]} onChange={(v) => setAnswer(cur.id, v)} token={token} />
          </div>
          <div className="nazorg-card__spacer" />
          <div className="nazorg-card__autosave">
            <span className="nazorg-meta__dot" aria-hidden="true" />
            {t("nazorg.autosave")}
          </div>
        </div>

        {submitError ? (
          <p className="nazorg-footer__error" role="alert">
            {submitError}
          </p>
        ) : null}
        <div className="nazorg-nav">
          {safeIdx > 0 ? (
            <button
              type="button"
              className="nazorg-ghost"
              disabled={busy}
              onClick={() => setIdx(Math.max(0, safeIdx - 1))}
            >
              {t("nazorg.back")}
            </button>
          ) : null}
          <div className="nazorg-nav__spacer" />
          <button type="button" className="nazorg-primary nazorg-primary--inline" disabled={busy} onClick={onNext}>
            {busy ? t("nazorg.submitting") : safeIdx >= N - 1 ? t("nazorg.finish") : t("nazorg.next")}
          </button>
        </div>
      </div>
      <Toast text={toast} />
    </div>,
    true,
  );
}

/** De lettertypestapels die bij de vier keuzes horen; dezelfde namen als in de backoffice. */
const LETTERSTAPEL: Record<string, string> = {
  SCHREEF: 'Georgia, "Times New Roman", serif',
  ROND: '"Trebuchet MS", "Segoe UI", system-ui, sans-serif',
  SMAL: '"Arial Narrow", "Segoe UI", system-ui, sans-serif',
};

/**
 * De huisstijl als css-variabelen op de portaalwortel. Alleen wat het asiel heeft ingevuld wordt gezet;
 * de rest blijft staan zoals styles.css het definieert, zodat een half ingevulde huisstijl nooit een
 * onleesbare pagina oplevert.
 */
function huisstijlStyle(huisstijl: NazorgHuisstijl | undefined, accent: string | undefined): React.CSSProperties | undefined {
  const stijl: Record<string, string> = {};
  const accentkleur = huisstijl?.accent || accent;
  if (accentkleur) stijl["--naz-accent"] = accentkleur;
  if (huisstijl?.achtergrond) stijl["--naz-bg"] = huisstijl.achtergrond;
  if (huisstijl?.oppervlak) stijl["--naz-surface"] = huisstijl.oppervlak;
  if (huisstijl?.tekst) stijl["--naz-ink"] = huisstijl.tekst;
  if (huisstijl?.primair) stijl["--naz-primair"] = huisstijl.primair;
  const stapel = huisstijl?.lettertype ? LETTERSTAPEL[huisstijl.lettertype] : undefined;
  if (stapel) stijl["fontFamily"] = stapel;
  return Object.keys(stijl).length > 0 ? (stijl as React.CSSProperties) : undefined;
}

/** The portal chrome: branded header + the optional "opslaan & later verder" link, then the screen. */
function PortalShell({
  wizard,
  showSave,
  onSave,
  children,
}: {
  wizard: NazorgWizard | null;
  showSave: boolean;
  onSave: () => void;
  children: React.ReactNode;
}) {
  const t = useTranslate();
  const orgName = wizard?.opvangNaam || t("shell.brand");
  const initial = orgName.trim().charAt(0).toUpperCase() || "·";
  // De huisstijl van het asiel wint van het losse accent uit de uitnodiging; staat er niets, dan
  // blijft alles zoals het portaal het van zichzelf doet.
  const stijl = huisstijlStyle(wizard?.huisstijl, wizard?.accent);
  const logoUrl = wizard?.huisstijl?.logoUrl;
  return (
    <main className="nazorg-portal" style={stijl}>
      <header className="nazorg-header">
        <div className="nazorg-header__brand">
          {logoUrl ? (
            <img className="nazorg-logo nazorg-logo--beeld" src={logoUrl} alt="" />
          ) : (
            <span className="nazorg-logo" aria-hidden="true">
              {initial}
            </span>
          )}
          <span className="nazorg-brandtext">
            <span className="nazorg-org">{orgName}</span>
            <span className="nazorg-sub">{t("nazorg.portalName")}</span>
          </span>
        </div>
        {showSave ? (
          <button type="button" className="nazorg-save" onClick={onSave}>
            <span className="nazorg-save__dot" aria-hidden="true" />
            {t("nazorg.saveLater")}
          </button>
        ) : null}
      </header>
      {children}
    </main>
  );
}

function Toast({ text }: { text: string }) {
  if (!text) {
    return null;
  }
  return (
    <div className="nazorg-toast" role="status">
      <span className="nazorg-toast__dot" aria-hidden="true" />
      {text}
    </div>
  );
}
